import React, { useState, useEffect } from 'react';
import { api } from '../../src/services/api-new';
import * as XLSX from 'xlsx';

// --- Types ---
interface Product {
    id: string;
    name: string;
    sku: string;
    category: 'Medication' | 'Vaccine' | 'Surgical' | 'Nutrition' | 'Retail' | string;
    price: number;
    stock: number;
    maxStock: number;
    threshold: number;
    image: string;
    lastRestocked: string;
    is_for_sale?: boolean | number;
}

interface AdminInventoryProps {
    products?: any[];
    services?: any[];
    onBack?: () => void;
}

const AdminInventory: React.FC<AdminInventoryProps> = ({ products: externalProducts, services: externalServices, onBack }) => {
    const [products, setProducts] = useState<Product[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<'All' | 'Low' | 'Out' | 'Internal' | 'Market'>('All');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [isDataLoading, setIsDataLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (externalProducts && externalProducts.length > 0) {
                setProducts(externalProducts as Product[]);
                setIsDataLoading(false);
                return;
            }

            setIsDataLoading(true);
            try {
                const response: any = await api.products.getAll();
                const productsData = response.products || response;
                setProducts(productsData);
            } catch (error) {
                console.error("Failed to fetch inventory:", error);
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, [externalProducts]);

    const handleEditProduct = (product: Product) => {
        setEditingProduct(product);
        setIsEditModalOpen(true);
    };

    const handleUpdateProduct = async (updatedProduct: Product) => {
        try {
            await api.products.update(updatedProduct.id, updatedProduct);
            setProducts(prev => prev.map(p => p.id === updatedProduct.id ? updatedProduct : p));
            setIsEditModalOpen(false);
            setEditingProduct(null);
        } catch (error) {
            console.error("Failed to update product:", error);
            alert("Failed to update product");
        }
    };

    const handleExportExcel = async () => {
        setIsDataLoading(true);
        try {
            const allData: any = await api.products.export();

            // Filter data based on active tab
            let dataToExport = allData;
            let filenameSuffix = 'full_inventory';

            if (activeTab === 'Internal') {
                dataToExport = allData.filter((p: any) => !p.is_for_sale || p.is_for_sale === 0 || p.is_for_sale === false);
                filenameSuffix = 'internal_supply';
            } else if (activeTab === 'Market') {
                dataToExport = allData.filter((p: any) => p.is_for_sale === 1 || p.is_for_sale === true);
                filenameSuffix = 'marketplace';
            } else if (activeTab === 'Low') {
                dataToExport = allData.filter((p: any) => p.stock <= p.threshold && p.stock > 0);
                filenameSuffix = 'low_stock';
            } else if (activeTab === 'Out') {
                dataToExport = allData.filter((p: any) => p.stock === 0);
                filenameSuffix = 'depleted';
            }

            // Map data to human-readable format for Excel
            let mappedData = [];

            if (dataToExport.length === 0) {
                // Generate template headers if no data
                mappedData = [{
                    'SKU': '',
                    'Product Name': '',
                    'Classification': '',
                    'Unit Price ($)': 0,
                    'Stock Level': 0,
                    'Max Capacity': 100,
                    'Alert Threshold': 10,
                    'Product Type (Marketplace/Internal)': activeTab === 'Internal' ? 'Internal' : 'Marketplace',
                    'Description': '',
                    'Image URL': ''
                }];
            } else {
                mappedData = dataToExport.map((p: any) => ({
                    'SKU': p.sku,
                    'Product Name': p.name,
                    'Classification': p.category,
                    'Unit Price ($)': p.price,
                    'Stock Level': p.stock,
                    'Max Capacity': p.maxStock || p.max_stock,
                    'Alert Threshold': p.threshold,
                    'Product Type (Marketplace/Internal)': (p.is_for_sale === 1 || p.is_for_sale === true) ? 'Marketplace' : 'Internal',
                    'Description': p.description || '',
                    'Image URL': p.image || ''
                }));
            }

            const worksheet = XLSX.utils.json_to_sheet(mappedData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, filenameSuffix.replace(/_/g, ' ').toUpperCase());
            XLSX.writeFile(workbook, `inventory_${filenameSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error("Failed to export inventory:", error);
            alert("Failed to export inventory");
        } finally {
            setIsDataLoading(false);
        }
    };

    const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const data = event.target?.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const rawResults: any[] = XLSX.utils.sheet_to_json(worksheet);

                if (rawResults.length > 0) {
                    const contextName = activeTab === 'Internal' ? 'Internal Supply' : activeTab === 'Market' ? 'Marketplace' : 'Inventory';
                    const confirmMsg = activeTab === 'Internal' || activeTab === 'Market'
                        ? `Are you sure you want to import ${rawResults.length} items AS ${contextName.toUpperCase()}? This will automatically set all items in this file to ${contextName}.`
                        : `Are you sure you want to import ${rawResults.length} items? This will update existing items matching SKU.`;

                    if (window.confirm(confirmMsg)) {
                        setIsDataLoading(true);
                        try {
                            // Map human-readable headers back to database fields
                            const sanitizedResults = rawResults.map(row => {
                                // Extract value for is_for_sale based on context or column
                                let isForSale = 1; // Default

                                if (activeTab === 'Internal') {
                                    isForSale = 0;
                                } else if (activeTab === 'Market') {
                                    isForSale = 1;
                                } else {
                                    const typeValue = row['Product Type (Marketplace/Internal)'] || row['Type'] || row['is_for_sale'];
                                    if (typeValue !== undefined) {
                                        const strValue = String(typeValue).toLowerCase();
                                        if (strValue === 'internal' || strValue === '0' || strValue === 'false' || strValue === 'internal supply') {
                                            isForSale = 0;
                                        } else {
                                            isForSale = 1;
                                        }
                                    }
                                }

                                return {
                                    sku: row['SKU'] || row.sku,
                                    name: row['Product Name'] || row.name || 'Untitled Product',
                                    category: row['Classification'] || row.category || 'Retail',
                                    price: Number(row['Unit Price ($)'] || row.price || 0),
                                    stock: Number(row['Stock Level'] || row.stock || 0),
                                    maxStock: Number(row['Max Capacity'] || row.max_stock || row.maxStock || 100),
                                    threshold: Number(row['Alert Threshold'] || row.threshold || 10),
                                    is_for_sale: isForSale,
                                    description: row['Description'] || row.description || '',
                                    image: row['Image URL'] || row.image || ''
                                };
                            });

                            await api.products.bulk(sanitizedResults);
                            // Refresh products
                            const response: any = await api.products.getAll();
                            const productsData = response.products || response;
                            setProducts(productsData);
                            alert(`Successfully imported ${rawResults.length} items to ${contextName}.`);
                        } catch (error) {
                            console.error("Failed to import Excel:", error);
                            alert("Failed to import Excel");
                        } finally {
                            setIsDataLoading(false);
                        }
                    }
                }
            } catch (error) {
                console.error("Error reading Excel file:", error);
                alert("Invalid Excel file");
            }
            // Clear input
            e.target.value = '';
        };
        reader.readAsBinaryString(file);
    };

    // Stats
    const lowStock = products.filter(p => p.stock <= p.threshold && p.stock > 0);
    const outOfStock = products.filter(p => p.stock === 0);
    const totalValue = products.reduce((acc, curr) => acc + (curr.price * curr.stock), 0);

    const filteredItems = products.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase());
        if (activeTab === 'Low') return matchesSearch && p.stock <= p.threshold && p.stock > 0;
        if (activeTab === 'Out') return matchesSearch && p.stock === 0;
        if (activeTab === 'Internal') return matchesSearch && (!p.is_for_sale || p.is_for_sale === 0 || p.is_for_sale === false);
        if (activeTab === 'Market') return matchesSearch && (p.is_for_sale === 1 || p.is_for_sale === true);
        return matchesSearch;
    });

    if (isDataLoading) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-background-dark">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Syncing Warehouse Inventory...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-slate-50 dark:bg-background-dark p-6 lg:p-10 custom-scrollbar">
            <div className="max-w-7xl mx-auto space-y-10">

                {/* Header section */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-surface-light dark:bg-background-dark border border-gray-100 dark:border-gray-800 hover:border-primary transition-all shadow-sm"
                            >
                                <span className="material-icons-round text-gray-600 dark:text-gray-300">arrow_back</span>
                            </button>
                        )}
                        <div>
                            <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-1">Clinic Operations</p>
                            <h1 className="text-4xl font-black text-primary-dark dark:text-white tracking-tight">Supply Chain</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleExportExcel}
                            className="bg-white dark:bg-surface-dark text-gray-500 border border-gray-200 dark:border-gray-800 px-6 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all hover:border-primary hover:text-primary flex items-center gap-2 shadow-sm"
                        >
                            <span className="material-icons-round text-lg">download</span> {activeTab === 'Internal' ? 'Export Internal Supply' : activeTab === 'Market' ? 'Export Marketplace' : 'Export Excel'}
                        </button>
                        <button
                            onClick={() => document.getElementById('excel-import-input')?.click()}
                            className="bg-white dark:bg-surface-dark text-gray-500 border border-gray-200 dark:border-gray-800 px-6 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all hover:border-primary hover:text-primary flex items-center gap-2 shadow-sm"
                        >
                            <span className="material-icons-round text-lg">upload</span> {activeTab === 'Internal' ? 'Import Internal Supply' : activeTab === 'Market' ? 'Import Marketplace' : 'Import Excel'}
                        </button>
                        <input
                            type="file"
                            id="excel-import-input"
                            accept=".xlsx, .xls"
                            className="hidden"
                            onChange={handleImportExcel}
                        />
                        <button
                            onClick={() => setIsAddModalOpen(true)}
                            className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 flex items-center gap-2 transition-all hover:-translate-y-1 active:scale-95"
                        >
                            <span className="material-icons-round text-lg">add_circle</span> Register Item
                        </button>
                    </div>
                </div>

                {/* Metrics Dashboard */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <MetricCard label="Inventory Valuation" value={`$${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} icon="account_balance_wallet" trend="+12.5%" trendColor="text-green-500" />
                    <MetricCard label="Stock Alert Items" value={lowStock.length} icon="error_outline" description={`${lowStock.length} items below threshold`} color="text-orange-500" />
                    <MetricCard label="Depleted Stocks" value={outOfStock.length} icon="production_quantity_limits" description="Critical reorder needed" color="text-red-500" />
                </div>

                {/* Main Content Card */}
                <div className="bg-white dark:bg-surface-dark rounded-[2.5rem] shadow-xl shadow-black/5 border border-gray-100 dark:border-gray-800 overflow-hidden">
                    <div className="p-8 border-b border-gray-100 dark:border-gray-800 flex flex-col xl:flex-row justify-between xl:items-center gap-6 bg-slate-50/50 dark:bg-black/10">
                        <div className="flex bg-white dark:bg-background-dark p-1.5 rounded-2xl shadow-inner border border-gray-100 dark:border-gray-800 w-fit no-scrollbar overflow-x-auto max-w-full">
                            <TabButton label="Full Inventory" active={activeTab === 'All'} onClick={() => setActiveTab('All')} count={products.length} />
                            <TabButton label="Internal Supply" active={activeTab === 'Internal'} onClick={() => setActiveTab('Internal')} count={products.filter(p => !p.is_for_sale || p.is_for_sale === 0 || p.is_for_sale === false).length} color="text-blue-500" />
                            <TabButton label="Marketplace" active={activeTab === 'Market'} onClick={() => setActiveTab('Market')} count={products.filter(p => p.is_for_sale === 1 || p.is_for_sale === true).length} color="text-purple-500" />
                            <TabButton label="Low Stock" active={activeTab === 'Low'} onClick={() => setActiveTab('Low')} count={lowStock.length} color="text-orange-500" />
                            <TabButton label="Depleted" active={activeTab === 'Out'} onClick={() => setActiveTab('Out')} count={outOfStock.length} color="text-red-500" />
                        </div>

                        <div className="flex items-center gap-4 flex-1 xl:max-w-md">
                            <div className="relative flex-1">
                                <span className="material-icons-round absolute left-4 top-3.5 text-gray-400">search</span>
                                <input
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-white dark:bg-background-dark border border-gray-100 dark:border-gray-700 rounded-2xl py-3.5 pl-12 pr-4 text-xs font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all placeholder:text-gray-300"
                                    placeholder="Search by SKU, Name or Category..."
                                />
                            </div>
                            <button className="w-12 h-12 bg-white dark:bg-background-dark border border-gray-100 dark:border-gray-800 rounded-2xl flex items-center justify-center text-gray-400 hover:text-primary transition-all shadow-sm">
                                <span className="material-icons-round">tune</span>
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto no-scrollbar">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-slate-50/30 dark:bg-black/5 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                    <th className="px-10 py-6">Product Information</th>
                                    <th className="px-6 py-6 text-center">SKU / Class</th>
                                    <th className="px-6 py-6 w-1/4">Availability Status</th>
                                    <th className="px-6 py-6 text-right">Unit Value</th>
                                    <th className="px-10 py-6 text-right">Operations</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50 text-[13px]">
                                {filteredItems.map(item => (
                                    <tr key={item.id} className="group hover:bg-primary/[0.01] dark:hover:bg-primary/[0.03] transition-all">
                                        <td className="px-10 py-6">
                                            <div className="flex items-center gap-5">
                                                <div className="relative">
                                                    <img src={item.image} className="w-14 h-14 rounded-2xl object-cover bg-gray-100 shadow-md group-hover:scale-105 transition-transform" alt={item.name} />
                                                    <div className={`absolute -top-2 -right-2 w-5 h-5 rounded-full border-2 border-white dark:border-surface-dark shadow-sm ${item.stock === 0 ? 'bg-red-500' : item.stock <= item.threshold ? 'bg-orange-500' : 'bg-green-500'}`}></div>
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <p className="font-black text-primary-dark dark:text-white whitespace-nowrap">{item.name}</p>
                                                        {(!item.is_for_sale || item.is_for_sale === 0) && (
                                                            <span className="bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter">Internal</span>
                                                        )}
                                                    </div>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Last restocked: {item.lastRestocked}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <div className="space-y-1.5 min-w-[100px]">
                                                <p className="font-mono text-[11px] font-black text-gray-500 uppercase">{item.sku}</p>
                                                <span className="bg-slate-100 dark:bg-gray-800 text-gray-500 text-[8px] font-black px-2 py-0.5 rounded tracking-widest uppercase inline-block">{item.category}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6">
                                            <div className="bg-slate-50 dark:bg-background-dark/50 p-4 rounded-2xl border border-gray-100/50 dark:border-gray-800/30 min-w-[180px]">
                                                <div className="flex justify-between items-end mb-2.5">
                                                    <p className="text-[10px] font-black uppercase text-gray-400">Current Level</p>
                                                    <p className="text-[11px] font-black text-primary-dark dark:text-white">
                                                        {item.stock} <span className="text-gray-400 font-bold">/ {item.maxStock}</span>
                                                    </p>
                                                </div>
                                                <div className="h-2.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden flex shadow-inner">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-1000 shadow-lg ${item.stock === 0 ? 'bg-red-500' : item.stock <= item.threshold ? 'bg-gradient-to-r from-orange-400 to-orange-600' : 'bg-gradient-to-r from-primary to-primary-dark'}`}
                                                        style={{ width: `${(item.stock / item.maxStock) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 text-right">
                                            <p className="font-black text-lg text-primary-dark dark:text-white font-mono whitespace-nowrap">${item.price.toFixed(2)}</p>
                                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">per unit</p>
                                        </td>
                                        <td className="px-10 py-6 text-right">
                                            <div className="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                                                <button
                                                    onClick={() => handleEditProduct(item)}
                                                    className="w-10 h-10 bg-white dark:bg-background-dark border border-gray-100 dark:border-gray-800 rounded-xl flex items-center justify-center text-gray-400 hover:text-primary transition-all hover:scale-110 active:scale-90 shadow-sm"
                                                >
                                                    <span className="material-icons-round text-lg">edit</span>
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        if (window.confirm('Delete this product?')) {
                                                            try {
                                                                await api.products.delete(item.id);
                                                                setProducts(prev => prev.filter(p => p.id !== item.id));
                                                            } catch (error) {
                                                                console.error("Failed to delete product:", error);
                                                                alert("Failed to delete product");
                                                            }
                                                        }
                                                    }}
                                                    className="w-10 h-10 bg-white dark:bg-background-dark border border-gray-100 dark:border-gray-800 rounded-xl flex items-center justify-center text-gray-400 hover:text-red-500 transition-all hover:scale-110 active:scale-95 shadow-sm"
                                                >
                                                    <span className="material-icons-round text-lg">delete</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* MODAL: ADD PRODUCT */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-primary-dark/40 backdrop-blur-xl animate-fade-in" onClick={() => setIsAddModalOpen(false)}>
                    <div className="bg-white dark:bg-surface-dark w-full max-w-3xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-up" onClick={e => e.stopPropagation()}>
                        <div className="p-10 border-b border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-black/20 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-1">Stock Entry</p>
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Register New Supply</h2>
                            </div>
                            <button onClick={() => setIsAddModalOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-center text-gray-400"><span className="material-icons-round">close</span></button>
                        </div>

                        <form className="p-10 space-y-10 overflow-y-auto custom-scrollbar no-scrollbar">
                            <div className="flex flex-col md:flex-row gap-10">
                                <div className="w-48 h-48 mx-auto md:mx-0 shrink-0 rounded-[2rem] bg-slate-50 dark:bg-background-dark/50 border-4 border-dashed border-gray-100 dark:border-gray-800 flex flex-col items-center justify-center text-gray-300 hover:border-primary hover:text-primary transition-all cursor-pointer group">
                                    <span className="material-icons-round text-5xl mb-3 group-hover:scale-110 transition-transform">add_photo_alternate</span>
                                    <span className="text-[10px] font-black uppercase tracking-widest">Upload Asset</span>
                                </div>

                                <div className="flex-1 space-y-8">
                                    <div>
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Product Nomenclature</label>
                                        <input id="name" className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-lg font-black outline-none placeholder:text-gray-300" placeholder="e.g. Heartgard Small Dog 12-pk" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-8">
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">SKU / Identifier</label>
                                            <input id="sku" className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-xs font-black outline-none font-mono" placeholder="INV-RX-00XXX" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Classification</label>
                                            <select id="category" className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-[10px] font-black uppercase outline-none">
                                                <option>Medication</option><option>Vaccine</option><option>Surgical</option><option>Nutrition</option><option>Retail</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                                <div className="md:col-span-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Pricing ($)</label>
                                    <input id="price" className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none" placeholder="0.00" />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Current Stock</label>
                                    <input id="stock" className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none" placeholder="0" />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Max Capacity</label>
                                    <input id="maxStock" className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none" placeholder="100" />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Alert Threshold</label>
                                    <input id="threshold" className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none" placeholder="10" />
                                </div>
                            </div>
                        </form>

                        <div className="p-10 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-4 bg-slate-50/50 dark:bg-black/20">
                            <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-8 py-4 font-black text-[10px] text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">Abort Entry</button>
                            <button
                                type="button"
                                onClick={async () => {
                                    const name = (document.getElementById('name') as HTMLInputElement)?.value;
                                    const sku = (document.getElementById('sku') as HTMLInputElement)?.value;
                                    const category = (document.getElementById('category') as HTMLSelectElement)?.value;
                                    const price = parseFloat((document.getElementById('price') as HTMLInputElement)?.value || '0');
                                    const stock = parseInt((document.getElementById('stock') as HTMLInputElement)?.value || '0');
                                    const maxStock = parseInt((document.getElementById('maxStock') as HTMLInputElement)?.value || '100');
                                    const threshold = parseInt((document.getElementById('threshold') as HTMLInputElement)?.value || '10');

                                    if (name) {
                                        try {
                                            const newProductData = {
                                                name,
                                                sku,
                                                category,
                                                price,
                                                stock,
                                                maxStock,
                                                threshold,
                                                image: 'https://images.unsplash.com/photo-1589924691195-41432c84c161?auto=format&fit=crop&w=200&q=80',
                                                lastRestocked: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                            };
                                            const result = await api.products.create(newProductData);
                                            // Depending on backend response structure, might need result.product or result directly
                                            const createdProduct = result.product || result;
                                            setProducts(prev => [...prev, createdProduct]);
                                            setIsAddModalOpen(false);
                                        } catch (error) {
                                            console.error("Failed to add product:", error);
                                            alert("Failed to add product");
                                        }
                                    }
                                }}
                                className="bg-primary text-white px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 transition-all hover:-translate-y-1 active:scale-95"
                            >
                                Verify & Commit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: EDIT PRODUCT */}
            {isEditModalOpen && editingProduct && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-primary-dark/40 backdrop-blur-xl animate-fade-in" onClick={() => setIsEditModalOpen(false)}>
                    <div className="bg-white dark:bg-surface-dark w-full max-w-3xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-up" onClick={e => e.stopPropagation()}>
                        <div className="p-10 border-b border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-black/20 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-1">Edit Entry</p>
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Edit Product</h2>
                            </div>
                            <button onClick={() => setIsEditModalOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-center text-gray-400"><span className="material-icons-round">close</span></button>
                        </div>

                        <form className="p-10 space-y-10 overflow-y-auto custom-scrollbar no-scrollbar">
                            <div className="flex flex-col md:flex-row gap-10">
                                <div className="w-48 h-48 mx-auto md:mx-0 shrink-0 rounded-[2rem] bg-slate-50 dark:bg-background-dark/50 border-4 border-dashed border-gray-100 dark:border-gray-800 flex flex-col items-center justify-center text-gray-300 hover:border-primary hover:text-primary transition-all cursor-pointer group">
                                    <span className="material-icons-round text-5xl mb-3 group-hover:scale-110 transition-transform">add_photo_alternate</span>
                                    <span className="text-[10px] font-black uppercase tracking-widest">Upload Asset</span>
                                </div>

                                <div className="flex-1 space-y-8">
                                    <div>
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Product Nomenclature</label>
                                        <input
                                            id="edit-name"
                                            defaultValue={editingProduct.name}
                                            className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-lg font-black outline-none placeholder:text-gray-300"
                                            placeholder="e.g. Heartgard Small Dog 12-pk"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-8">
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">SKU / Identifier</label>
                                            <input
                                                id="edit-sku"
                                                defaultValue={editingProduct.sku}
                                                className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-xs font-black outline-none font-mono"
                                                placeholder="INV-RX-00XXX"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Classification</label>
                                            <select
                                                id="edit-category"
                                                defaultValue={editingProduct.category}
                                                className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-[10px] font-black uppercase outline-none"
                                            >
                                                <option>Medication</option><option>Vaccine</option><option>Surgical</option><option>Nutrition</option><option>Retail</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                                <div className="md:col-span-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Pricing ($)</label>
                                    <input
                                        id="edit-price"
                                        type="number"
                                        step="0.01"
                                        defaultValue={editingProduct.price}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Current Stock</label>
                                    <input
                                        id="edit-stock"
                                        type="number"
                                        defaultValue={editingProduct.stock}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none"
                                        placeholder="0"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Max Capacity</label>
                                    <input
                                        id="edit-maxStock"
                                        type="number"
                                        defaultValue={editingProduct.maxStock}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none"
                                        placeholder="100"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Alert Threshold</label>
                                    <input
                                        id="edit-threshold"
                                        type="number"
                                        defaultValue={editingProduct.threshold}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 p-5 rounded-2xl text-sm font-black outline-none"
                                        placeholder="10"
                                    />
                                </div>
                                <div className="md:col-span-1 border-l border-gray-100 dark:border-gray-800 pl-8 flex flex-col justify-center">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Market Visibility</label>
                                    <label className="flex items-center gap-3 cursor-pointer group">
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                id="edit-is_for_sale"
                                                defaultChecked={editingProduct.is_for_sale === 1 || editingProduct.is_for_sale === true}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary shadow-inner"></div>
                                        </div>
                                        <span className="text-xs font-black text-gray-500 group-hover:text-primary transition-colors">For Sale</span>
                                    </label>
                                </div>
                            </div>
                        </form>

                        <div className="p-10 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-4 bg-slate-50/50 dark:bg-black/20">
                            <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-8 py-4 font-black text-[10px] text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">Cancel</button>
                            <button
                                type="button"
                                onClick={async () => {
                                    const name = (document.getElementById('edit-name') as HTMLInputElement)?.value;
                                    const sku = (document.getElementById('edit-sku') as HTMLInputElement)?.value;
                                    const category = (document.getElementById('edit-category') as HTMLSelectElement)?.value;
                                    const price = parseFloat((document.getElementById('edit-price') as HTMLInputElement)?.value || '0');
                                    const stock = parseInt((document.getElementById('edit-stock') as HTMLInputElement)?.value || '0');
                                    const maxStock = parseInt((document.getElementById('edit-maxStock') as HTMLInputElement)?.value || '100');
                                    const threshold = parseInt((document.getElementById('edit-threshold') as HTMLInputElement)?.value || '10');
                                    const is_for_sale = (document.getElementById('edit-is_for_sale') as HTMLInputElement)?.checked;

                                    if (name) {
                                        try {
                                            const updatedProductData = {
                                                ...editingProduct,
                                                name,
                                                sku,
                                                category,
                                                price,
                                                stock,
                                                maxStock,
                                                threshold,
                                                is_for_sale,
                                                image: editingProduct.image,
                                                lastRestocked: editingProduct.lastRestocked
                                            };
                                            await handleUpdateProduct(updatedProductData);
                                        } catch (error) {
                                            console.error("Failed to update product:", error);
                                            alert("Failed to update product");
                                        }
                                    }
                                }}
                                className="bg-primary text-white px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 transition-all hover:-translate-y-1 active:scale-95"
                            >
                                Update Product
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

// --- Sub-components ---
const MetricCard = ({ label, value, icon, description, trend, trendColor, color }: any) => (
    <div className="bg-white dark:bg-surface-dark p-8 rounded-[2.5rem] shadow-xl shadow-black/5 border border-gray-100 dark:border-gray-800 flex items-center gap-6 group hover:border-primary/20 transition-all">
        <div className={`w-16 h-16 rounded-[1.5rem] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform ${color ? 'bg-slate-50 dark:bg-black/20' : 'bg-primary/10 text-primary'}`}>
            <span className={`material-icons-round text-2xl ${color || 'text-primary'}`}>{icon}</span>
        </div>
        <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">{label}</p>
            <div className="flex items-center gap-3">
                <h3 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">{value}</h3>
                {trend && <span className={`text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-50 dark:bg-gray-800 ${trendColor}`}>{trend}</span>}
            </div>
            {description && <p className="text-[10px] font-bold text-gray-500 mt-1 uppercase italic opacity-60">{description}</p>}
        </div>
    </div>
);

const TabButton = ({ label, active, onClick, count, color }: any) => (
    <button
        onClick={onClick}
        className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2.5 whitespace-nowrap ${active ? 'bg-slate-100 dark:bg-gray-800 text-primary-dark dark:text-white shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
    >
        <span className={active ? (color || 'text-primary-dark dark:text-white') : ''}>{label}</span>
        {count !== undefined && <span className={`text-[9px] px-2 py-0.5 rounded-full font-black ${active ? 'bg-primary text-white shadow-md shadow-primary/30' : 'bg-slate-100 dark:bg-gray-800 text-gray-400'}`}>{count}</span>}
    </button>
);

export default AdminInventory;